/*
Muestra la página de edición de vehiculos, 
y envía los datos al servidor.
Autor: Benjamín León
Fecha: 2024-08-28
*/
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import useVehicleFetcher from '../hooks/useVehicleFetcher'
import Vehicle from '../interfaces/Vehicle'
import VehicleDto from '../interfaces/VehicleDto'
import vehicleTypeTranslate from '../utils/vehicleTypeTranslate'
import fuelTranslate from '../utils/fuelTranslate'
import stateTranslate from '../utils/stateTranslate'
import permitTranslate from '../utils/permitTranslate'
import insuranceTranslate from '../utils/insuranceTranslate'
import characteristicTranslate from '../utils/characteristicTranslate'
import maintenanceTranslate from '../utils/maintenanceTranslate'
import { useAuth } from '../hooks/useAuth'
import { formatWithThousandSep, getBodiesByType, vehicleHrefReturn } from '../utils/utils'
import BodyByType from '../interfaces/BodyByType'
import { FormatProps, handleInputChangeFormat } from '../utils/handleInputChangeFormat'
import { Rule, validateFields } from '../utils/validateForm'
import gearboxTranslate from '../utils/gearboxTranslate'
import colorTranslate from '../utils/colorTranslate'
import useVehiclePoster from '../hooks/useVehiclePoster'
import Brand from '../interfaces/Brand'
import useBrandsFetcher from '../hooks/useBrandsFetcher'
import Model from '../interfaces/Model'
import useModelsFetcher from '../hooks/useModelsFetcher'
import { VehicleState } from '../enums/DocStates'
import { PageState } from '../interfaces/PageState'
import { useStrictValidation } from '../hooks/useStrictValidation'
import { AppPath } from '../enums/AppPath'
import { mapGetApiColor, mapGetApiFuel, mapGetApiTransmission, mapGetApiType } from '../utils/getapiMappers'
import { useGetApi } from '../hooks/useGetApi'

import SelectBranchField from './SelectBranchField'
import ConfirmationModal from './ConfirmationModal'
import DisplayError from './DisplayError'
import { ButtonAction } from './Buttons'
import { SelectField } from './SelectField'
import InputField from './InputField'
import CheckboxField from './CheckBoxField'

import { UserProfileRole } from '@/modules/users/types/UserProfile'

export interface VehicleEx extends VehicleDto {
  year_format: string
  suggestedPrice_format: string
  odometer_format: string
  owners_format: string
  keys_format: string
  price_format: string
  appraisal_format: string
}
const VehicleEdit: React.FC<{
  id?: string
  mode: boolean
  onReturn?: (id: string) => void
  pageState?: PageState
}> = ({ id, mode, onReturn, pageState }) => {
  const [confirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false)
  const [btnEditDisabled, setBtnEditDisabled] = useState<boolean>(true)
  const { errorVehicle, loading, fetchVehicle } = useVehicleFetcher()
  const { fetchVehicleByPlate, loading: loadingGetApi, error: errorGetApi } = useGetApi()
  const { errorPostVehicle, postVehicle } = useVehiclePoster()
  const { errorBrands, fetchBrands } = useBrandsFetcher()
  const { errorModels, fetchModels } = useModelsFetcher()
  const { userData, vehicleForDoc, selectedPair, setSelectedPair, hasRole } = useAuth()
  const [formData, setFormData] = useState<VehicleEx | null>(null)
  const [originalVehicle, setOriginalVehicle] = useState<Vehicle | null>(null)
  const [localError, setLocalError] = useState<string | null>(null)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [brands, setBrands] = useState<Brand[]>([])
  const [allBrands, setAllBrands] = useState<Brand[]>([])
  // Mantiene los modelos de una marca - tipo
  const [models, setModels] = useState<Model[]>([])
  // Mantiene todos los models
  const [allModels, setAllModels] = useState<Model[]>([])
  const [lastSearchedPlate, setLastSearchedPlate] = useState<string>('')
  const { setIsDirty } = useAuth()

  /*
  Permiso para tocar el precio.

  Hasta el 31/08/2026 cualquiera que llegara a esta pantalla podia cambiarlo. Sin el
  permiso el campo se ve —el precio es informacion que el vendedor necesita— pero no se
  puede editar.

  `mode` es true al MODIFICAR y false al CREAR (ver makeUrlVehicleEdit). La restriccion
  es solo para modificar: quien carga un vehiculo nuevo tiene que poder ponerle precio, y
  los flujos de nota de venta y reserva crean desde esta misma pantalla con mode=false.

  Esto es solo la UI. Lo que de verdad impide el cambio esta en la API
  (vehicleEditRoute), porque el body lo arma el cliente y un campo deshabilitado no
  detiene a nadie que use curl.
  */
  const canModifyPrice = !mode || hasRole(UserProfileRole.ModifyVehiclePrice)

  useEffect(() => {
    if (!id && mode) {
      setLocalError('ID is not defined.')

      return // Exit early if `id` is undefined y estamos en "edit"
    }

    if (id && mode) {
      // Aca cuando es edit
      const fetchDataAsync = async () => {
        const vehicleFull = await fetchVehicle(id)

        if (vehicleFull) {
          setOriginalVehicle(vehicleFull)
          setFormData(initVehicle(vehicleFull))
          setLastSearchedPlate(vehicleFull.plate || '')
        } else {
          setLocalError('Vehicle data not found.')
        }
      }
      fetchDataAsync()
    } else {
      // Create
      setFormData(initVehicle(null))
    }
  }, [id])

  useEffect(() => {
    const fetchBrandsAsync = async () => {
      const allBrands = await fetchBrands()

      if (allBrands) {
        setAllBrands(allBrands)
      }
    }
    fetchBrandsAsync()
  }, [])

  useEffect(() => {
    if (formData && allBrands) {
      const brands = allBrands.filter((b) => b.type === formData.type)
      setBrands(brands)
      setModels([])
    }
  }, [formData?.type, allBrands])

  useEffect(() => {
    const fetchModelsAsync = async () => {
      const alldata = await fetchModels()

      if (alldata) {
        setAllModels(alldata)
      }
    }
    fetchModelsAsync()
  }, [])

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!btnEditDisabled) {
        event.preventDefault()
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [btnEditDisabled])

  const handleInputChangeEx = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setBtnEditDisabled(false)
    setIsDirty(true)
    const formatProps: FormatProps[] = [
      { name: 'year', name_format: 'year_format', isInteger: true },
      { name: 'suggestedPrice', name_format: 'suggestedPrice_format' },
      { name: 'odometer', name_format: 'odometer_format' },
      { name: 'owners', name_format: 'owners_format' },
      { name: 'keys', name_format: 'keys_format' },
      { name: 'price', name_format: 'price_format' },
      { name: 'appraisal', name_format: 'appraisal_format' },
    ]
    handleInputChangeFormat<VehicleEx>(e, formData, setFormData, formatProps)
  }

  const handleGetApiSearch = async () => {
    // Deshabilitar la consulta a GetAPI para esta organización en específico
    if (userData?.organization?._id === '6823bd0b4537f1046999ab2d') {
      return
    }

    if (!formData?.plate) {
      setLocalError('Debes ingresar una patente para consultar la base de datos')

      return
    }

    const data = await fetchVehicleByPlate(formData.plate)

    if (data && formData) {
      setBtnEditDisabled(false)
      setIsDirty(true)

      const newType = mapGetApiType(data.model?.typeVehicle?.name || '')

      setFormData({
        ...formData,
        year: data.year,
        year_format: data.year ? data.year.toString() : '',
        chassisId: data.vinNumber || formData.chassisId,
        engineId: data.engineNumber || formData.engineId,
        brand: data.model?.brand?.name || formData.brand,
        model: data.model?.name || formData.model,
        version: data.version || formData.version,
        color: mapGetApiColor(data.color || '') || formData.color,
        gearbox: mapGetApiTransmission(data.transmission || '') || formData.gearbox,
        fuel: mapGetApiFuel(data.fuel || '') || formData.fuel,
        type: newType || formData.type,
      })
      // Clear any auto-complete suggestions that might override this
      setSuggestions([])
      setModelSuggestions([])
    }
  }

  // Auto-trigger GetAPI when plate hits exactly 6 characters without needing blur
  useEffect(() => {
    if (formData?.plate && formData.plate.length === 6 && !loadingGetApi && formData.plate !== lastSearchedPlate) {
      setLastSearchedPlate(formData.plate)
      handleGetApiSearch()
    }
  }, [formData?.plate])

  const handlePlateBlur = () => {
    // Solo si tiene al menos 6 y es distinto de 6 (ya que 6 se dispara con el useEffect)
    if (formData?.plate && formData.plate.length > 6 && formData.plate !== lastSearchedPlate) {
      setLastSearchedPlate(formData.plate)
      handleGetApiSearch()
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setBtnEditDisabled(false)
    setIsDirty(true)
    const { name, value, type } = e.target

    if (formData) {
      setFormData(() => {
        let updatedValue: string | number | Date = value

        if (type === 'number') {
          updatedValue = Number(value)
        }
        // Handle nested fields such as 'documents.checkup'
        const keys = name.split('.')

        if (keys.length > 1) {
          // For nested fields, like 'documents.checkup'
          // Check if the property is an object
          const firstKey = keys[0] as keyof VehicleDto
          const secondKey = keys[1]
          // Ensure prevState[firstKey] is an object before spreading
          const nestedObject = formData[firstKey]

          if (typeof nestedObject === 'object' && nestedObject !== null) {
            return {
              ...formData,
              [firstKey]: {
                ...nestedObject, // Safely spread only if it's an object
                [secondKey]: updatedValue,
              },
            }
          } else {
            return {
              ...formData,
              [firstKey]: {
                [secondKey]: updatedValue,
              },
            }
          }
        } else {
          return {
            ...formData,
            [name]: updatedValue,
          }
        }
      })
    }
  }

  // Handle checkbox change
  const handleCheckChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target
    setBtnEditDisabled(false)
    setIsDirty(true)
    setFormData(() => {
      if (!formData) return null // Safety check

      const updatedCharacteristics = checked
        ? [...formData.characteristics, value]
        : formData.characteristics.filter((characteristic) => characteristic !== value)

      return {
        ...formData,
        characteristics: updatedCharacteristics,
      }
    })
  }
  const navigate = useNavigate()

  // Click para crear o moodificar
  const handleEditClick = async () => {
    if (btnEditDisabled) return

    if (!formData) return
    // Validemos antes Define the fields to validate

    const fields = [
      { name: 'brand', value: formData.brand, rule: Rule.REQUIRED },
      { name: 'model', value: formData.model, rule: Rule.REQUIRED },
      { name: 'color', value: formData.color, rule: Rule.REQUIRED },
      { name: 'plate', value: formData.plate, rule: Rule.REQUIRED },
      { name: 'documents.checkup', value: formData.documents?.checkup, rule: Rule.REQUIRED },

      { name: 'year_format', value: formData.year_format, rule: Rule.GREATER_ZERO },
      { name: 'suggestedPrice_format', value: formData.suggestedPrice, rule: Rule.GREATER_ZERO },
      { name: 'odometer_format', value: formData.odometer, rule: Rule.GREATER_ZERO },
      { name: 'owners_format', value: formData.owners, rule: Rule.GREATER_ZERO },
      { name: 'keys_format', value: formData.keys, rule: Rule.GREATER_ZERO },
      { name: 'price_format', value: formData.price, rule: Rule.GREATER_ZERO },
      { name: 'appraisal_format', value: formData.appraisal, rule: Rule.GREATER_ZERO },
      // los combos
      { name: 'type', value: formData.type, rule: Rule.REQUIRED },
      { name: 'body', value: formData.body, rule: Rule.REQUIRED },
      { name: 'gearbox', value: formData.gearbox, rule: Rule.REQUIRED },
      { name: 'fuel', value: formData.fuel, rule: Rule.REQUIRED },
      { name: 'state', value: formData.state, rule: Rule.REQUIRED },
      { name: 'branch', value: formData.branch, rule: Rule.REQUIRED },
      { name: 'documents.insurance', value: formData.documents.insurance, rule: Rule.REQUIRED },
      { name: 'documents.permit', value: formData.documents.permit, rule: Rule.REQUIRED },
      { name: 'maintenance.status', value: formData.maintenance.status, rule: Rule.REQUIRED },
    ]

    const validationErrors = validateFields(fields)

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors) // Set errors if any
      window.scrollTo({ top: 0, behavior: 'smooth' })

      return
    }

    setErrors({})

    // cuando no es modificar, entonces debemos rescatar
    /// los valores profile y organization desde userData
    if (!formData) return

    if (!mode && formData) {
      formData.organization = userData?.organization?._id ?? ''
      formData.profile = userData?.profile?._id ?? ''
    }
    const response = await postVehicle(formData, mode)

    if (!response) {
      return
    }

    const id = mode ? formData._id : response

    if (!mode && onReturn) {
      // Aca cuando estamos creando y siendo llamados por
      // la reserva
      onReturn(id)

      return
    }

    // Si estamos creando para crear un documento, entonces tenemos
    // que ir de vuelta a la VehCliSelection con este vehiculos
    const goTo: string = vehicleHrefReturn(vehicleForDoc, id)
    // Si estamos creando, ademas tenemos que setear el vehiculo creado
    // en la variable global selectedPair
    const vehicleIdAux = vehicleForDoc > 0 ? id : ''

    setSelectedPair({ ...selectedPair, vehicleId: vehicleIdAux })

    //TODO: Por ahora, solo si modificamos le agregamos el estado
    if (mode) {
      navigate(goTo, { state: pageState })
    } else {
      navigate(goTo)
    }
    setIsDirty(false)
  }

  // Strict validation for published vehicles
  const strictValidation = useStrictValidation(originalVehicle, formData?.type || '', userData?.bodies)

  let bodiesByType: BodyByType[] = []

  if (userData?.bodies) {
    bodiesByType =
      strictValidation.bodyTypeOptions.length > 0
        ? strictValidation.bodyTypeOptions
        : formData?.type
          ? getBodiesByType(formData?.type, userData.bodies)
          : []
  }

  // Color options for all vehicles - corrected to save user-friendly values
  // Transform { 'COLOR.GRAY': 'Gris' } → { 'Gris': 'Gris' } so form saves display values
  const colorOptions = Object.fromEntries(Object.entries(colorTranslate).map(([, value]) => [value, value]))
  // para el autocomplete de las marcas (brand)
  // const brands = ['Ford', 'Fiat', 'Ferrari', 'BMW', 'Audi', 'Chevrolet', 'Toyota', 'Tesla'];

  const [suggestions, setSuggestions] = useState<Brand[]>([])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = e.target.value
    handleInputChange(e)
    setModelSuggestions([])

    if (value) {
      const filteredSuggestions = brands.filter((brand) => brand.name.toLowerCase().startsWith(value.toLowerCase()))
      setSuggestions(filteredSuggestions)
    } else {
      setSuggestions([])
    }
  }
  // Cuando el usuario cambia el tipo (auto, moto, etc)
  const handleTypeChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = e.target.value

    if (value && formData) {
      setFormData({ ...formData, type: value, brand: '', model: '' })
    }
  }
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // If there's exactly one suggestion and the user presses Tab or ArrowRight, autocomplete
    if (!formData) return
    // limpiamos el modelo
    setFormData({ ...formData, model: '' })

    if ((e.key === 'Tab' || e.key === 'ArrowRight') && suggestions.length === 1) {
      e.preventDefault() // Prevent default behavior (e.g., tabbing out of the input)
      const name = e.currentTarget.name
      setFormData({ ...formData, [name]: suggestions[0].name })
      setSuggestions([]) // Clear suggestions after autocomplete
      // tenemos que recargar la tabla de modelos de esta marca
      const models = allModels.filter((b) => b.brand === suggestions[0]._id)
      setModels(models)
    }
  }

  // CLick en la sugerencia de la marca
  const handleSuggestionClick = (suggestion: string, brand_id: string) => {
    if (!formData) return
    setFormData({ ...formData, brand: suggestion })
    setSuggestions([])
    // filtramos los modelos de esta marca, porque a veces el usuario
    // no hace "Tab" para irse a los modelos
    const models = allModels.filter((b) => b.brand === brand_id)
    setModels(models)
  }
  // end autocomplete marcas
  // para el autocomplete de los modelos

  const [modelSuggestions, setModelSuggestions] = useState<Model[]>([])

  const handleModelChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = e.target.value
    handleInputChange(e)

    if (value) {
      const filteredSuggestions = models.filter((model) => model.name.toLowerCase().startsWith(value.toLowerCase()))
      setModelSuggestions(filteredSuggestions)
    } else {
      setModelSuggestions([])
    }
  }

  const handleModelKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // If there's exactly one suggestion and the user presses Tab or ArrowRight, autocomplete
    if (!formData) return

    if ((e.key === 'Tab' || e.key === 'ArrowRight') && modelSuggestions.length === 1) {
      e.preventDefault() // Prevent default behavior (e.g., tabbing out of the input)
      const name = e.currentTarget.name
      setFormData({ ...formData, [name]: modelSuggestions[0].name })
      setModelSuggestions([]) // Clear suggestions after autocomplete
    }
  }

  const handleModelSuggestionClick = (suggestion: string) => {
    if (!formData) return
    setFormData({ ...formData, model: suggestion })
    setModelSuggestions([])
  }
  // end autocomplete marcas
  const stateTranslateEx = (doc: number) => {
    if (doc === VehicleState.Consignables) {
      return Object.fromEntries(Object.entries(stateTranslate).filter(([key]) => key !== 'STATE.VEHICLE.PURCHASED'))
    }

    // Si lo estamos creando para comprar O si lo estamos creando justo en el momento de
    ///una reserva o venta. En este ultimo caso viene "onReturn"
    if (doc === VehicleState.Comprables || onReturn) {
      return Object.fromEntries(Object.entries(stateTranslate).filter(([key]) => key === 'STATE.VEHICLE.PURCHASED'))
    }

    return stateTranslate
  }

  // Nos devolvemos
  const handleGoBack = () => {
    if (btnEditDisabled) {
      handleConfirmGoBack()
    } else {
      setConfirmationModalOpen(true)
    }
  }

  const handleConfirmGoBack = () => {
    setIsDirty(false)
    setConfirmationModalOpen(false)

    if (mode) {
      navigate(`/${AppPath.Vehicles}/view/${id}`, { state: pageState })
    } else if (onReturn) {
      onReturn('')

      return
    } else if (pageState) {
      const stateString = JSON.stringify(pageState)
      const encodedState = encodeURIComponent(stateString)
      navigate(`${pageState.url}?pageState=${encodedState}`)
    }
  }

  return (
    <div className="p-4">
      {loading && <div>Loading...</div>}
      {errorVehicle && <div>Error: {errorVehicle.message}</div>}

      {localError && <div>Error: {localError}</div>}

      {/* Screen Freeze Overlay when GetAPI is loading */}
      {loadingGetApi && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30 backdrop-blur-sm">
          <div className="bg-white p-6 rounded-lg shadow-xl flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-lg font-semibold text-gray-700">Obteniendo datos del vehículo...</p>
          </div>
        </div>
      )}

      {/* First Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <InputField
          label="Placa"
          name="plate"
          value={formData?.plate}
          type="text"
          handleInputChange={handleInputChange}
          onBlur={handlePlateBlur}
          errors={errors}
        />

        <SelectField
          label="Tipo"
          name="type"
          value={formData?.type}
          options={vehicleTypeTranslate}
          handleInputChange={handleTypeChange}
          errors={errors}
        />

        <InputField
          label="ID del Chasis"
          name="chassisId"
          value={formData?.chassisId}
          type="text"
          handleInputChange={handleInputChange}
        />

        <InputField
          label="ID del Motor"
          name="engineId"
          value={formData?.engineId}
          type="text"
          handleInputChange={handleInputChange}
        />

        <div className="relative">
          <InputField
            label="Marca"
            name="brand"
            value={formData?.brand}
            type="text"
            handleInputChange={handleChange}
            errors={errors}
            inputClassName="px-4 py-2 border rounded"
            handleKeyDown={handleKeyDown}
          />

          {suggestions.length > 0 && (
            <ul className="z-10 absolute left-0 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
              {suggestions.map((suggestion, index) => (
                <li
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion.name, suggestion._id)}
                  className="px-4 py-2 cursor-pointer hover:bg-gray-200"
                >
                  {suggestion.name}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="relative">
          <InputField
            label="Modelo"
            name="model"
            value={formData?.model}
            type="text"
            handleInputChange={handleModelChange}
            errors={errors}
            inputClassName="px-4 py-2 border rounded"
            handleKeyDown={handleModelKeyDown}
          />

          {modelSuggestions.length > 0 && (
            <ul className="z-10 absolute left-0 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
              {modelSuggestions.map((suggestion, index) => (
                <li
                  key={index}
                  onClick={() => handleModelSuggestionClick(suggestion.name)}
                  className="px-4 py-2 cursor-pointer hover:bg-gray-200"
                >
                  {suggestion.name}
                </li>
              ))}
            </ul>
          )}
        </div>
        <InputField
          label="Versión"
          name="version"
          value={formData?.version}
          type="text"
          handleInputChange={handleInputChange}
          errors={errors}
        />
        <InputField
          label="Año"
          name="year_format"
          value={formData?.year_format}
          type="string"
          handleInputChange={handleInputChangeEx}
          errors={errors}
        />
        <SelectBodyField
          label="Carrocería"
          name="body"
          value={formData?.body}
          options={bodiesByType}
          handleInputChange={handleInputChange}
          errors={errors}
        />
        <SelectField
          label="Color"
          name="color"
          value={formData?.color}
          options={colorOptions}
          handleInputChange={handleInputChange}
          errors={errors}
        />
        <InputField
          label="Kilometraje"
          name="odometer_format"
          value={formData?.odometer_format}
          type="string"
          handleInputChange={handleInputChangeEx}
          errors={errors}
        />
        <SelectField
          label="Transmisión"
          name="gearbox"
          value={formData?.gearbox}
          options={gearboxTranslate}
          handleInputChange={handleInputChange}
          errors={errors}
        />
        <SelectFuelField
          label="Combustible"
          name="fuel"
          value={formData?.fuel}
          fuelOptions={strictValidation.fuelTypeOptions}
          regularOptions={fuelTranslate}
          isStrictMode={strictValidation.isStrictMode}
          handleInputChange={handleInputChange}
          errors={errors}
        />
        <InputField
          label="Propietarios"
          name="owners_format"
          value={formData?.owners_format}
          type="string"
          handleInputChange={handleInputChangeEx}
          errors={errors}
        />
        <InputField
          label="Llaves"
          name="keys_format"
          value={formData?.keys_format}
          type="string"
          handleInputChange={handleInputChangeEx}
          errors={errors}
        />
        <SelectBranchField
          label="Sucursal"
          name="branch"
          value={formData?.branch}
          options={userData?.branches}
          handleInputChange={handleInputChange}
          errors={errors}
        />
        <SelectField
          label="Estado"
          name="state"
          value={formData?.state}
          options={stateTranslateEx(vehicleForDoc)}
          handleInputChange={handleInputChange}
          errors={errors}
        />
        <InputField
          label="Avalúo"
          name="appraisal_format"
          value={formData?.appraisal_format}
          type="string"
          handleInputChange={handleInputChangeEx}
          errors={errors}
        />
        <InputField
          label="Precio"
          name="price_format"
          value={formData?.price_format}
          type="string"
          handleInputChange={handleInputChangeEx}
          errors={errors}
          disabled={!canModifyPrice}
        />
        <InputField
          label="Precio Sugerido"
          name="suggestedPrice_format"
          value={formData?.suggestedPrice_format}
          type="string"
          handleInputChange={handleInputChangeEx}
          errors={errors}
        />
        <SelectField
          label="Seguro"
          name="documents.insurance"
          value={formData?.documents?.insurance}
          options={insuranceTranslate}
          handleInputChange={handleInputChange}
          errors={errors}
        />
        <SelectField
          label="Permiso"
          name="documents.permit"
          value={formData?.documents?.permit}
          options={permitTranslate}
          handleInputChange={handleInputChange}
          errors={errors}
        />
        <InputField
          label="Revisión"
          name="documents.checkup"
          value={formData?.documents?.checkup}
          type="date"
          handleInputChange={handleInputChange}
          errors={errors}
        />
        <SelectField
          label="Mantenimiento"
          name="maintenance.status"
          value={formData?.maintenance?.status}
          options={maintenanceTranslate}
          handleInputChange={handleInputChange}
          errors={errors}
        />
        <div className="col-span-2">
          <InputField
            label="Comentarios Mantenimiento"
            name="maintenance.comments"
            value={formData?.maintenance?.comments}
            type="text"
            handleInputChange={handleInputChange}
          />
        </div>
        <div className="col-span-2">
          <InputField
            label="Comentarios Generales"
            name="comments"
            value={formData?.comments}
            type="text"
            handleInputChange={handleInputChange}
          />
        </div>
      </div>

      {/* Second Section */}
      <div className="mt-8">
        <h2 className="font-bold text-xl mb-4">Características</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(characteristicTranslate).map(([key, label]) => (
            <CheckboxField
              key={key}
              name="characteristics"
              value={key}
              label={label}
              checked={formData?.characteristics?.includes(key) ?? false}
              handleInputChange={handleCheckChange}
            />
          ))}
        </div>
      </div>
      {errorPostVehicle && <DisplayError msg={errorPostVehicle.message} />}
      {errorGetApi && <DisplayError msg={errorGetApi} />}

      <div className="flex justify-center mt-4 gap-4">
        <ButtonAction
          label={`${mode ? 'Actualizar' : 'Crear'}`}
          handleButtonClick={handleEditClick}
          disabled={btnEditDisabled}
        />
        <ButtonAction label="Volver" handleButtonClick={handleGoBack} />
        {/*pageState && (
          <ButtonAction label="Volver" handleButtonClick={handleGoBack} />
        )*/}
      </div>
      <div>{errorBrands?.message}</div>
      <div>{errorModels?.message}</div>
      {confirmationModalOpen && (
        <ConfirmationModal
          message={`Tienes datos no guardados. ¿Seguro que quieres salir?`}
          onConfirm={handleConfirmGoBack}
          onCancel={() => setConfirmationModalOpen(false)}
        />
      )}
    </div>
  )
}

// Reusable Select Field Component para el Body
const SelectBodyField: React.FC<{
  label: string
  name: string
  value?: string
  options: BodyByType[]
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void
  errors?: { [key: string]: string }
}> = ({ label, name, value, options, handleInputChange, errors }) => {
  // Convert array to record
  const optionsRecord: Record<string, string> = options.reduce(
    (acc, option) => {
      acc[option.body] = option.description

      return acc
    },
    {} as Record<string, string>
  )

  return (
    <SelectField
      label={label}
      name={name}
      value={value}
      options={optionsRecord}
      handleInputChange={handleInputChange}
      errors={errors}
    />
  )
}

// Reusable Select Field Component para el Fuel
const SelectFuelField: React.FC<{
  label: string
  name: string
  value?: string
  fuelOptions: { fuel: string; description: string }[]
  regularOptions: Record<string, string>
  isStrictMode: boolean
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void
  errors?: { [key: string]: string }
}> = ({ label, name, value, fuelOptions, regularOptions, isStrictMode, handleInputChange, errors }) => {
  // Convert fuel options to record when in strict mode
  const optionsRecord: Record<string, string> = isStrictMode
    ? fuelOptions.reduce(
        (acc, option) => {
          acc[option.fuel] = option.description

          return acc
        },
        {} as Record<string, string>
      )
    : regularOptions

  return (
    <SelectField
      label={label}
      name={name}
      value={value}
      options={optionsRecord}
      handleInputChange={handleInputChange}
      errors={errors}
    />
  )
}

const initVehicle = (vehicle: VehicleDto | null): VehicleEx => {
  const year = vehicle?.year ?? 0
  const year_format = year === 0 ? '' : year.toString()
  const suggestedPrice = vehicle?.suggestedPrice ?? 0
  const suggestedPrice_format = suggestedPrice === 0 ? '' : formatWithThousandSep(suggestedPrice)
  const odometer = vehicle?.odometer ?? 0
  const odometer_format = odometer === 0 ? '' : formatWithThousandSep(odometer)
  const owners = vehicle?.owners ?? 0
  const owners_format = owners === 0 ? '' : formatWithThousandSep(owners)
  const keys = vehicle?.keys ?? 0
  const keys_format = keys === 0 ? '' : formatWithThousandSep(keys)
  const price = vehicle?.price ?? 0
  const price_format = price === 0 ? '' : formatWithThousandSep(price)
  const appraisal = vehicle?.appraisal ?? 0
  const appraisal_format = appraisal === 0 ? '' : formatWithThousandSep(appraisal)

  return {
    _id: vehicle?._id ?? '',
    organization: vehicle?.organization ?? '',
    profile: vehicle?.profile ?? '',
    brand: vehicle?.brand ?? '',
    model: vehicle?.model ?? '',
    version: vehicle?.version ?? '',
    year: year,
    type: vehicle?.type ?? '',
    body: vehicle?.body ?? '',
    color: vehicle?.color ?? '',
    odometer: odometer,
    gearbox: vehicle?.gearbox ?? '',
    fuel: vehicle?.fuel ?? '',
    plate: vehicle?.plate ?? '',
    chassisId: vehicle?.chassisId ?? '',
    engineId: vehicle?.engineId ?? '',
    owners: owners,
    keys: keys,
    branch: vehicle?.branch ?? '',
    state: vehicle?.state ?? '',
    appraisal: appraisal,
    price: price,
    suggestedPrice: suggestedPrice,
    documents: {
      insurance: vehicle?.documents?.insurance ?? '',
      permit: vehicle?.documents?.permit ?? '',
      checkup: vehicle?.documents?.checkup ?? '',
    },
    maintenance: {
      status: vehicle?.maintenance?.status ?? '',
      comments: vehicle?.maintenance?.comments ?? '',
    },
    comments: vehicle?.comments ?? '',
    characteristics: vehicle?.characteristics ?? [],
    year_format: year_format,
    suggestedPrice_format: suggestedPrice_format,
    odometer_format: odometer_format,
    owners_format: owners_format,
    keys_format: keys_format,
    price_format: price_format,
    appraisal_format: appraisal_format,
  }
}

export default VehicleEdit
